import XLSX from 'xlsx-js-style';
import {
    describeBasis,
    describeNotices,
    describeRowCheck,
    WORK_ROUTINE_MISSING_TEXT,
    FIXED_GUIDES,
    formatHandoffs,
    formatKst,
    getNoticeCount,
    checkNeededCount,
    isCheckNeededRow,
    isUnpaidUnknown,
    UNPAID_UNKNOWN_TEXT,
    SESSION_REPORT_SHEETS,
} from './labels';
import { SessionReport, SessionReportCoach, SessionReportRow, SessionReportSheetTotals } from './types';

/**
 * 「총페이롤」 엑셀 — 고객이 손으로 만들던 파일과 같은 열 위치로 쓴다.
 * 계약: BACKEND_HANDOFF_STAGE1.md §3. 고객 원본: 「8월 윤준희 페이롤 완.xlsx」(강사 시트), 「총 페이롤.xlsx」(총괄).
 *
 * 원칙
 * - **값만 쓴다(수식 없음).** 남은세션을 수식으로 쓰면 서버가 잡은 검산 차이가 지워진다.
 * - **숫자는 서버 값 그대로.** 더하거나 부호를 바꾸지 않는다. 합계 줄도 서버 `totals`·`centerTotals`.
 * - 강사 시트는 B~T 가 고객 원본과 같은 자리다(P·Q 는 고객의 타임비 수식 자리라 비운다).
 *   그 오른쪽 U 「소멸」, V 「확인」은 라포가 덧붙이는 참고 열이다.
 */

type CellValue = string | number | null | undefined;

const AMOUNT_FORMAT = '#,##0';
const BORDER = { style: 'thin', color: { rgb: 'D9D9D9' } };
const BORDERS = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const HEADER_STYLE = {
    font: { bold: true },
    fill: { fgColor: { rgb: 'F0F2F3' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: BORDERS,
};
const MISMATCH_FILL = { fgColor: { rgb: 'FFF8E1' } };

/** 강사 시트 4행 머리글. 키 = 열 글자. 고객 원본과 같은 자리. */
export const COACH_SHEET_HEADERS: Record<string, string> = {
    B: '회원명',
    C: '전월잔여',
    D: '구분',
    E: '등록금액',
    F: '세션',
    G: 'IN',
    H: 'OUT',
    I: '진행세션',
    J: '남은세션',
    K: '담당',
    L: '인계세션',
    M: '특수매출',
    N: '환불금액',
    O: '환불세션',
    P: '타임비',
    R: '미수',
    S: '금액(현재 기준)',
    T: '미수세션',
    U: '소멸',
    V: '확인',
};

/**
 * 총괄 시트 2행 머리글. 고객 「총 페이롤」의 B~X 자리.
 * 고객 원본의 V~X 는 FC(회원권) 매출 칸이다 — V2:W2 를 합친 「FC」(V=구분, W=금액), X「인원」. 세션 현황은 FC 를 계산하지 않으므로
 * 자리만 원본과 같게 비워 두고, 우리만 쓰는 소멸(참고)·확인 필요는 원본에 없는 Z·AA 로 뺐다(Y 는 원본의 메모 칸).
 */
export const SUMMARY_SHEET_HEADERS: Record<string, string> = {
    B: '강사명',
    C: '금액',
    D: '신규',
    E: '금액',
    F: '변경',
    G: '금액',
    H: '리뉴',
    I: '인계 내역',
    J: '세션',
    K: '계약미수금(현재 기준)',
    L: '전월잔여',
    M: '당월등록',
    N: '당월완료',
    O: '남은세션',
    P: 'IN',
    Q: 'OUT',
    R: '환불금액',
    S: '세션',
    T: '특수매출',
    U: '특매환불',
    V: 'FC',
    W: '',
    X: '인원',
    Z: '소멸(참고)',
    AA: '확인 필요',
};

/** 고객 원본처럼 V2:W2 를 합친다(「FC」 한 칸). */
const SUMMARY_SHEET_MERGES = ['V2:W2'];

const AMOUNT_COLUMNS_COACH = new Set(['E', 'N', 'S']);
const AMOUNT_COLUMNS_SUMMARY = new Set(['C', 'E', 'G', 'K', 'R', 'T', 'U']);

const colIndex = (col: string) => XLSX.utils.decode_col(col);

class SheetWriter {
    ws: XLSX.WorkSheet = {};
    private maxRow = 0;
    private maxCol = 0;

    set(col: string, row: number, value: CellValue, style?: Record<string, unknown>, amount = false) {
        const c = colIndex(col);
        const r = row - 1;
        this.maxRow = Math.max(this.maxRow, r);
        this.maxCol = Math.max(this.maxCol, c);
        const address = XLSX.utils.encode_cell({ c, r });
        if (value === null || value === undefined || value === '') {
            if (style) this.ws[address] = { t: 's', v: '', s: style };
            return;
        }
        const cell: XLSX.CellObject & { s?: unknown } =
            typeof value === 'number' ? { t: 'n', v: value } : { t: 's', v: String(value) };
        if (typeof value === 'number' && amount) cell.z = AMOUNT_FORMAT;
        if (style) cell.s = style;
        this.ws[address] = cell;
    }

    finish(colWidths: Record<string, number>) {
        this.ws['!ref'] = XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: this.maxCol, r: this.maxRow } });
        const cols: XLSX.ColInfo[] = [];
        for (let i = 0; i <= this.maxCol; i += 1) cols.push({ wch: colWidths[XLSX.utils.encode_col(i)] ?? 9 });
        this.ws['!cols'] = cols;
        return this.ws;
    }
}

/** 0 이면 빈 칸 — 고객 원본이 활동 없는 칸을 비워 두는 모양을 따른다(값 자체는 바꾸지 않는다). */
const blankIfZero = (n: number | null | undefined): CellValue => (n === 0 || n === null || n === undefined ? '' : n);

const yearMonthParts = (yearMonth: string) => {
    const [y, m] = yearMonth.split('-');
    return { year: y, month: Number(m) };
};

/** 시트 이름 규칙: 31자, 금지문자 `[]:*?/\` 제거, 같은 이름은 「 (2)」. */
/**
 * 강사 시트 3행 「PT+특수」 값 — 고객 원본 수식(등록금액 + 특수매출 + 환불금액)과 같은 기준.
 * 서버 시트 합계 세 값의 단순 합이다. 환불금액은 계약상 음수라 그대로 더하고,
 * 혹시 양수로 오면 빼서 항상 차감되게 한다. 특수매출이 없으면 0.
 */
export const ptPlusSpecialAmount = (totals: SessionReportSheetTotals) => {
    const refund = totals.refundAmount ?? 0;
    return totals.registeredAmount + (totals.specialSales ?? 0) + (refund <= 0 ? refund : -refund);
};

export const makeSheetNamer = () => {
    const used = new Set<string>();
    return (base: string, suffix = '') => {
        const clean = base.replace(/[[\]:*?/\\]/g, '').trim() || '강사';
        const room = 31 - suffix.length;
        let name = `${clean.slice(0, Math.max(1, room))}${suffix}`;
        let n = 2;
        while (used.has(name)) {
            const tag = ` (${n})`;
            name = `${clean.slice(0, Math.max(1, room - tag.length))}${tag}${suffix}`;
            n += 1;
        }
        used.add(name);
        return name;
    };
};

const writeCoachSheet = (
    report: SessionReport,
    coach: SessionReportCoach,
    sheet: { rows: SessionReportRow[] | null; totals: SessionReportSheetTotals },
) => {
    const w = new SheetWriter();
    const { month } = yearMonthParts(report.yearMonth);
    const basis = describeBasis({
        yearMonth: report.yearMonth,
        generatedAt: report.generatedAt,
        changedAfterMonthEnd: getNoticeCount(report.notices, 'CHANGED_AFTER_MONTH_END'),
    });

    // 2행: 기준 시각 안내
    w.set('B', 2, basis, { font: { color: { rgb: '6B7684' } } });
    // 3행: 월 | PT{강사} | PT매출 | 값 | 특수매출 | (빈) | PT+특수 | 값
    // PT매출 값 = 이 시트 등록금액 열 합계(서버 totals.registeredAmount). 고객 원본 G3=SUM(E106)과 같은 기준.
    w.set('B', 3, `${month}월`, { font: { bold: true } });
    w.set('C', 3, `PT${coach.coachName}`, { font: { bold: true } });
    w.set('E', 3, 'PT매출', { font: { bold: true } });
    w.set('G', 3, sheet.totals.registeredAmount, undefined, true);
    w.set('I', 3, '특수매출', { font: { bold: true } });
    w.set('M', 3, 'PT+특수', { font: { bold: true } });
    w.set('N', 3, ptPlusSpecialAmount(sheet.totals), undefined, true);
    // 4행: 머리글
    Object.entries(COACH_SHEET_HEADERS).forEach(([col, title]) => w.set(col, 4, title, HEADER_STYLE));
    w.set('Q', 4, '', HEADER_STYLE);

    let r = 5;
    for (const row of sheet.rows ?? []) {
        const style = isCheckNeededRow({ ...row, resolved: false }) ? { fill: MISMATCH_FILL } : undefined;
        const rowIndex = r;
        const put = (col: string, v: CellValue) => w.set(col, rowIndex, v, style, AMOUNT_COLUMNS_COACH.has(col));
        put('B', row.memberName);
        put('C', row.prevRemaining);
        put('D', row.kindLabel);
        put('E', blankIfZero(row.registeredAmount));
        put('F', blankIfZero(row.registeredSessions));
        put('G', blankIfZero(row.inCount));
        put('H', blankIfZero(row.outCount));
        put('I', row.progressed);
        put('J', row.remaining);
        put('K', formatHandoffs(row.handoffs));
        put('L', blankIfZero(row.handoffSessions));
        put('M', '');
        put('N', blankIfZero(row.refundAmount));
        put('O', blankIfZero(row.refundSessions));
        put('P', '');
        put('Q', '');
        put('R', '');
        // null = 서버가 계산하지 않은 미수. 0 이나 빈 칸으로 바꾸지 않는다.
        put('S', isUnpaidUnknown(row) ? UNPAID_UNKNOWN_TEXT : blankIfZero(row.unpaidAmount));
        put('T', '');
        put('U', blankIfZero(row.otherAdjustments?.closed));
        put('V', [describeRowCheck(row, { workRoutineMissing: coach.workRoutineMissing }), row.resolved === true ? '처리 완료' : ''].filter(Boolean).join(' · '));
        r += 1;
    }

    // 합계: 고객 원본처럼 이름 줄 + 값 줄(서버 totals 그대로)
    r += 1;
    const t = sheet.totals;
    const labelStyle = { ...HEADER_STYLE };
    const totalStyle = { font: { bold: true }, border: BORDERS };
    const labels: Record<string, string> = {
        B: '합계',
        C: '총 전월잔여',
        E: '총 등록금액',
        F: '총세션',
        G: 'IN',
        H: 'OUT',
        I: '총 진행세션',
        J: '총 남은세션',
        L: '총 인계세션',
        N: '총 환불금액',
        O: '총 환불세션',
        S: '총 미수(현재 기준)',
        U: '총 소멸',
    };
    Object.entries(labels).forEach(([col, title]) => w.set(col, r, title, labelStyle));
    r += 1;
    const totals: Record<string, number> = {
        C: t.prevRemaining,
        E: t.registeredAmount,
        F: t.registeredSessions,
        G: t.inCount,
        H: t.outCount,
        I: t.progressed,
        J: t.remaining,
        L: t.handoffSessions,
        N: t.refundAmount,
        O: t.refundSessions,
        S: t.unpaidAmount,
        U: t.closed,
    };
    w.set('B', r, `${t.rowCount}명`, totalStyle);
    Object.entries(totals).forEach(([col, v]) => w.set(col, r, v, totalStyle, AMOUNT_COLUMNS_COACH.has(col)));

    return w.finish({
        A: 2,
        B: 12,
        C: 9,
        D: 24,
        E: 13,
        F: 7,
        G: 6,
        H: 6,
        I: 9,
        J: 9,
        K: 20,
        L: 9,
        M: 9,
        N: 12,
        O: 9,
        P: 12,
        Q: 12,
        R: 8,
        S: 14,
        T: 9,
        U: 7,
        V: 40,
    });
};

const writeSummarySheet = (report: SessionReport, centerName: string) => {
    const w = new SheetWriter();
    const { month } = yearMonthParts(report.yearMonth);
    w.set('B', 1, `${month}월`, { font: { bold: true, sz: 14 } });
    w.set('C', 1, `${centerName ? `${centerName} ` : ''}PT 정산 · 세션 현황`, { font: { bold: true, sz: 14 } });
    Object.entries(SUMMARY_SHEET_HEADERS).forEach(([col, title]) => w.set(col, 2, title, HEADER_STYLE));

    let r = 3;
    const wrap = { alignment: { wrapText: true, vertical: 'top' } };
    for (const coach of report.coaches) {
        const s = coach.summary;
        const rowIndex = r;
        const put = (col: string, v: CellValue, style?: Record<string, unknown>) =>
            w.set(col, rowIndex, v, style, AMOUNT_COLUMNS_SUMMARY.has(col));
        put('B', coach.coachName);
        put('C', blankIfZero(s.newAmount));
        put('D', blankIfZero(s.newSessions));
        put('E', blankIfZero(s.upgradeAmount));
        put('F', blankIfZero(s.upgradeSessions));
        put('G', blankIfZero(s.renewAmount));
        put('H', blankIfZero(s.renewSessions));
        // 고객 원본과 같이 셀 안 줄바꿈
        put('I', formatHandoffs(s.handoffs, '\n'), wrap);
        put('J', blankIfZero(s.handoffSessions));
        put('K', blankIfZero(s.unpaidAmount));
        put('L', s.prevRemaining);
        put('M', s.registeredSessions);
        put('N', s.progressed);
        put('O', s.remaining);
        put('P', blankIfZero(s.inCount));
        put('Q', blankIfZero(s.outCount));
        put('R', blankIfZero(s.refundAmount));
        put('S', blankIfZero(s.refundSessions));
        put('T', s.specialSales ?? '');
        put('U', s.specialRefund ?? '');
        put('Z', blankIfZero(s.closed));
        put(
            'AA',
            [
                checkNeededCount(s.reference) ? `${checkNeededCount(s.reference)}건` : '',
                coach.workRoutineMissing ? WORK_ROUTINE_MISSING_TEXT : '',
            ]
                .filter(Boolean)
                .join(' · '),
        );
        r += 1;
    }

    // 센터 합계(서버 centerTotals 그대로 — 강사 줄을 더하지 않는다). 강사 미지정 조회에서만 온다.
    const c = report.centerTotals;
    if (!c) throw new Error('센터 합계가 없는 응답으로는 총괄 시트를 만들 수 없어요(강사 미지정 조회 필요).');
    const totalStyle = { font: { bold: true }, fill: { fgColor: { rgb: 'F0F2F3' } }, border: BORDERS };
    const totals: Record<string, CellValue> = {
        B: '센터 합계',
        C: c.newAmount,
        D: c.newSessions,
        E: c.upgradeAmount,
        F: c.upgradeSessions,
        G: c.renewAmount,
        H: c.renewSessions,
        I: '',
        J: c.handoffSessions,
        K: c.unpaidAmount,
        L: c.prevRemaining,
        M: c.registeredSessions,
        N: c.progressed,
        O: c.remaining,
        P: c.inCount,
        Q: c.outCount,
        R: c.refundAmount,
        S: c.refundSessions,
        T: c.specialSales ?? '',
        U: c.specialRefund ?? '',
        V: '',
        W: '',
        X: '',
        Y: '',
        Z: c.closed,
        AA: checkNeededCount(c.reference) ? `${checkNeededCount(c.reference)}건` : '',
    };
    Object.entries(totals).forEach(([col, v]) => w.set(col, r, v, totalStyle, AMOUNT_COLUMNS_SUMMARY.has(col)));

    // 아래: 기준 시각 안내·공지·고정 안내
    r += 2;
    const note = { font: { color: { rgb: '6B7684' } } };
    w.set(
        'B',
        r,
        describeBasis({
            yearMonth: report.yearMonth,
            generatedAt: report.generatedAt,
            changedAfterMonthEnd: getNoticeCount(report.notices, 'CHANGED_AFTER_MONTH_END'),
        }),
        note,
    );
    r += 1;
    w.set('B', r, 'PT 수강권만 집계했어요(그룹 수업 제외). 소멸(참고)은 삭제·발급 취소로 사라진 회차예요. FC·인원 칸은 직접 채우는 자리라 비워 뒀어요.', note);
    for (const text of [...describeNotices(report.notices), ...FIXED_GUIDES]) {
        r += 1;
        w.set('B', r, `· ${text}`, note);
    }

    const ws = w.finish({
        A: 2,
        B: 12,
        C: 13,
        D: 7,
        E: 13,
        F: 7,
        G: 13,
        H: 7,
        I: 18,
        J: 7,
        K: 14,
        L: 9,
        M: 9,
        N: 9,
        O: 9,
        P: 7,
        Q: 7,
        R: 13,
        S: 7,
        T: 11,
        U: 11,
        V: 8,
        W: 10,
        X: 7,
        Y: 4,
        Z: 10,
        AA: 14,
    });
    ws['!merges'] = SUMMARY_SHEET_MERGES.map((range) => XLSX.utils.decode_range(range));
    return ws;
};

/** 강사별 조회 응답에서 그 강사 항목을 꺼낸다(회원 행은 강사 지정 조회에만 있다). */
export const pickCoachFromReport = (coachReport: SessionReport | undefined, coachId: string) =>
    coachReport?.rowsIncluded === false ? undefined : coachReport?.coaches.find((c) => c.coachId === coachId);

/**
 * 총괄 시트는 강사 미지정 요약 응답(`summaryReport`)으로, 강사 시트는 강사별 응답(`coachReports[coachId]`)으로 쓴다.
 * 강사 하나라도 회원 행이 없으면 만들지 않는다(빠진 강사가 있는 파일을 건네지 않는다) — 호출부가 먼저 거른다.
 */
export const buildSessionReportWorkbook = (
    summaryReport: SessionReport,
    coachReports: Record<string, SessionReport>,
    centerName = '',
) => {
    const missing = summaryReport.coaches.filter((c) => !pickCoachFromReport(coachReports[c.coachId], c.coachId));
    if (missing.length > 0) {
        throw new Error(`회원별 자료가 없는 강사: ${missing.map((c) => c.coachName).join(', ')}`);
    }
    const workbook = XLSX.utils.book_new();
    const nameSheet = makeSheetNamer();
    XLSX.utils.book_append_sheet(workbook, writeSummarySheet(summaryReport, centerName), nameSheet('총괄'));
    for (const summaryCoach of summaryReport.coaches) {
        const coachReport = coachReports[summaryCoach.coachId];
        const coach = pickCoachFromReport(coachReport, summaryCoach.coachId) as SessionReportCoach;
        for (const { key, title } of SESSION_REPORT_SHEETS) {
            XLSX.utils.book_append_sheet(
                workbook,
                writeCoachSheet(coachReport, coach, coach.sheets[key]),
                nameSheet(summaryCoach.coachName, `_${title}`),
            );
        }
    }
    return workbook;
};

export const buildSessionReportFileName = (report: SessionReport, centerName = '') => {
    const { year, month } = yearMonthParts(report.yearMonth);
    const stamp = formatKst(report.generatedAt, 'YYYY-MM-DD HH.mm');
    const center = centerName.replace(/[\\/:*?"<>|]/g, '').trim();
    return `${year}년 ${String(month).padStart(2, '0')}월${center ? ` ${center}` : ''} 세션 현황 (${stamp}).xlsx`;
};
